import { useSetRecoilState } from 'recoil';
import {
  GetAllMediaQuery,
  MediaExportQuery,
  MediaResponse,
  SmartQuerySearchIndex,
  SmartQueryBody,
  SmartQueryBodyPaginated,
  OperationIds,
  ContentPatchBody
} from '../../../API';
import { serializeSmartQuery } from '../../../utils/queryBuilder';
import { Paginated, useLocales } from '../../../hooks';
import { MediaAPI } from '../../../hooks/API/Media/MediaAPI';
import { useNotificationSubscription } from '../../../hooks/API/NotificationSubscription/useNotificationSubscription';
import {
  createDefaultSearchParams,
  DataManagerHook,
  DataManagerHookReturnType,
  IDataManagerHookProps,
  MAX_GET_BY_IDS,
  searchParamsStorageKey,
  useGetRecoilState
} from '../../DataManager';
import { removeLocalStorageItem } from '../../../state/localStorageState';
import { CacheManager } from '../../DataManager/CacheManager';
import { DataManagerHelpers } from '../../DataManager/DataManagerHelpers';
import { saveAsFile } from '../../../utils/saveAsFile';
import { chunk, cloneDeep, debounce, flatten } from 'lodash-es';
import { isChannel, isMatch } from '../../../utils/mediaUtilities';
import { useContentServiceAPI } from '../../../hooks/API/ContentService/useContentServiceAPI';
import { LIMIT_OPTIONS } from '../../../components/shared/Pagination';

// Absent means the patch did not touch labels. null clears them. An empty list is stored as null
// because the API rejects `{ default: [] }`.
type SentContentLabels = { default: unknown[] } | null;

// Omit, don't intersect: once codegen adds contentLabels to MediaResponse, an intersection drops null
// and the clear value no longer assigns.
type MediaWithSentContentLabels = Omit<MediaResponse, 'contentLabels'> & {
  contentLabels?: SentContentLabels;
};

function contentLabelsSentInPatch(body: ContentPatchBody): SentContentLabels | undefined {
  if (!Object.prototype.hasOwnProperty.call(body, 'contentLabels')) return undefined;
  const sent = (body as ContentPatchBody & { contentLabels?: { default?: unknown[] } | null }).contentLabels;
  if (!sent?.default?.length) return null;
  return { default: sent.default };
}

export interface MediaHookReturnType extends DataManagerHookReturnType<MediaResponse> {
  searchMedia: (params: GetAllMediaQuery) => Promise<Paginated<MediaResponse> | undefined>;
  searchMediaBySmartQuery: (query: SmartQueryBodyPaginated) => Promise<Paginated<MediaResponse> | undefined>;
  exportMediaToCSV: (query: MediaExportQuery) => Promise<void>;
  getMediaByCollectionId: (collectionId: string) => Promise<MediaResponse[] | undefined>;
  getMediaBySmartQuery: (query: SmartQueryBody) => Promise<MediaResponse[] | undefined>;
  getContentSubscriptionStatus: (contentId: string) => Promise<boolean | undefined>;
  setContentSubscriptionStatus: (contentId: string, status: boolean) => Promise<boolean>;
  queueIdToFetch: (id: string, includeDeleted?: boolean) => void;
  getChannelsByIds: (ids: string[], includeDeleted?: boolean) => Promise<MediaResponse[] | undefined>;
  getTransmissionsByIds: (ids: string[], includeDeleted?: boolean) => Promise<MediaResponse[] | undefined>;
  patchContent: (contentId: string, body: ContentPatchBody) => Promise<MediaResponse | null | undefined>;
  clearPersistedSearchParams: () => void;
}

export function MediaHook(params: IDataManagerHookProps<MediaResponse>): MediaHookReturnType {
  const dataManagerHook = DataManagerHook<MediaResponse>(params);
  const api = params.useApiHook() as MediaAPI;
  const notificationSubscriptionApi = useNotificationSubscription();
  const contentServiceApi = useContentServiceAPI();

  const setCache = useSetRecoilState(params.state.withDataCache);
  const { addRecordsToCache, addRecordToCache, flattenCache } = CacheManager(params.idField, setCache);

  const { t } = useLocales();
  const { getRecordString, handleApiError, handleSuccess, missingActionError } = DataManagerHelpers({
    name: params.name,
    toString
  });

  const setIsSaving = useSetRecoilState(params.state.withIsSaving);
  const getCache = useGetRecoilState(params.state.withDataCache);
  const setSearchParams = useSetRecoilState(params.state.withSearchParams);

  const transmissionIdsToFetch: Set<string> = new Set();
  const channelIdsToFetch: Set<string> = new Set();

  const queueIdToFetch = (id: string, includeDeleted = false) => {
    if (isMatch(id)) {
      transmissionIdsToFetch.add(id);
      fetchAllTransmissionsFromQueue(includeDeleted);
    } else if (isChannel(id)) {
      channelIdsToFetch.add(id);
      fetchAllChannelsFromQueue(includeDeleted);
    } else {
      dataManagerHook.queueIdToFetch(id, includeDeleted);
    }
  };

  const fetchAllChannelsFromQueue = debounce((includeDeleted: boolean) => {
    const idsCopy = [...channelIdsToFetch];
    channelIdsToFetch.clear();
    fetchChannelsByIds(idsCopy, includeDeleted);
  }, 50);

  const fetchAllTransmissionsFromQueue = debounce((includeDeleted: boolean) => {
    const idsCopy = [...transmissionIdsToFetch];
    transmissionIdsToFetch.clear();
    fetchTransmissionsByIds(idsCopy, includeDeleted);
  }, 50);

  const searchMedia = async (params: GetAllMediaQuery) => {
    const action = 'fetch_paginated';
    try {
      if (!dataManagerHook.fetchPaginatedPost) throw missingActionError(action);
      const { limit: limitParam } = params;
      const [minLimit, maxLimit] = [Math.min(...LIMIT_OPTIONS), Math.max(...LIMIT_OPTIONS)];
      if (!limitParam || limitParam > maxLimit) {
        params.limit = maxLimit;
      } else if (limitParam < minLimit) {
        params.limit = minLimit;
      }
      return await dataManagerHook.fetchPaginatedPost(params);
    } catch (err) {
      console.error(err);
      handleApiError(err, 'get_paginated', JSON.stringify(params));
    }
  };

  const searchMediaBySmartQuery = async (query: SmartQueryBodyPaginated) => {
    const action = 'search_by_smart_query_paginated';
    try {
      const { limit: limitParam } = query;
      const [minLimit, maxLimit] = [Math.min(...LIMIT_OPTIONS), Math.max(...LIMIT_OPTIONS)];
      if (!limitParam || limitParam > maxLimit) {
        query.limit = maxLimit;
      } else if (limitParam < minLimit) {
        query.limit = minLimit;
      }

      const serializedQuery = serializeSmartQuery(query) as SmartQueryBodyPaginated;
      const response = await api.getBySmartQueryPaginated(serializedQuery);
      const paginatedData = response.data.body as Paginated<MediaResponse>;
      addRecordsToCache(paginatedData.results);
      return paginatedData;
    } catch (err) {
      console.error(err);
      handleApiError(err, action, `${t('query_builder.query')}: ${JSON.stringify(query)}`);
    }
  };

  const exportMediaToCSV = async (query: MediaExportQuery) => {
    try {
      const response = await api.exportCsv(query);
      saveAsFile(response);
    } catch (err) {
      console.error(err);
      handleApiError(err, 'export_media_to_csv', `${t('query_builder.query')}: ${query}`);
    }
  };

  const getMediaByCollectionId = async (collectionId: string) => {
    try {
      const response = await api.getByCollectionId(collectionId);
      addRecordsToCache(response.data.body);
      return response.data.body;
    } catch (err) {
      console.error(err);
      handleApiError(err, 'get_media_by_collection_id', collectionId);
    }
  };

  const getMediaBySmartQuery = async (query: SmartQueryBody) => {
    try {
      const serializedQuery = serializeSmartQuery(query);
      const fetchedMedia = query
        ? (
            await api.getBySmartQuery({
              ...serializedQuery,
              searchIndex: serializedQuery.searchIndex || SmartQuerySearchIndex.CONTENT
            })
          ).data.body
        : [];
      addRecordsToCache(fetchedMedia);
      return fetchedMedia;
    } catch (err) {
      console.error(err);
      handleApiError(err, 'get_by_smart_query', `${t('query_builder.query')}: ${query}`);
    }
  };

  const getContentSubscriptionStatus = async (contentId: string) => {
    try {
      const { data } = await notificationSubscriptionApi.getById(contentId);
      return data.body.isEnabled;
    } catch (err) {
      console.error(err);
      handleApiError(err, 'get_content_subscription_status', contentId);
    }
  };

  const setContentSubscriptionStatus = async (contentId: string, isEnabled: boolean) => {
    try {
      if (isEnabled) {
        await notificationSubscriptionApi.create(contentId);
      } else {
        await notificationSubscriptionApi.remove(contentId);
      }
      return true;
    } catch (err) {
      console.error(err);
      handleApiError(err, 'set_content_subscription_status', contentId);
      return false;
    }
  };

  const getChannelsByIds = async (ids: string[], includeDeleted = false) => {
    if (!ids.length) return;
    const cachedRecords = await dataManagerHook.getCachedRecordsToFetch(ids);
    if (cachedRecords.length > 0) {
      return flattenCache(cachedRecords);
    }
    const fetchedRecords = await fetchChannelsByIds(ids, includeDeleted);
    if (!fetchedRecords) {
      return flattenCache(cachedRecords);
    }
    return ids.map((id, i) => cachedRecords[i]?.object ?? fetchedRecords[id]?.object);
  };

  const getTransmissionsByIds = async (ids: string[], includeDeleted = false) => {
    if (!ids.length) return;
    const cachedRecords = await dataManagerHook.getCachedRecordsToFetch(ids);
    if (cachedRecords.length > 0) {
      return flattenCache(cachedRecords);
    }
    const fetchedRecords = await fetchTransmissionsByIds(ids, includeDeleted);
    if (!fetchedRecords) {
      return flattenCache(cachedRecords);
    }
    return ids.map((id, i) => cachedRecords[i]?.object ?? fetchedRecords[id]?.object);
  };

  const fetchIdsByAction = async (
    ids: string[],
    includeDeleted = false,
    action: OperationIds.GET_CHANNELS_BY_IDS | OperationIds.GET_TRANSMISSIONS_BY_IDS
  ) => {
    try {
      if (!api[action]) {
        throw missingActionError(action);
      }
      const chunkedIds = chunk(ids, MAX_GET_BY_IDS);
      const promises = chunkedIds.map((cIds) => api[action](cIds, includeDeleted));
      const responses = await Promise.all(promises);
      const records = flatten(responses.map((response) => response.data.body));
      return addRecordsToCache(records);
    } catch (err) {
      handleApiError(err, action, ids);
    }
  };

  const fetchChannelsByIds = async (ids: string[], includeDeleted = false) => {
    const action = OperationIds.GET_CHANNELS_BY_IDS;
    return fetchIdsByAction(ids, includeDeleted, action);
  };

  const fetchTransmissionsByIds = async (ids: string[], includeDeleted = false) => {
    const action = OperationIds.GET_TRANSMISSIONS_BY_IDS;
    return fetchIdsByAction(ids, includeDeleted, action);
  };

  const patchContent = async (contentId: string, body: ContentPatchBody) => {
    const action = OperationIds.PATCH;
    const cache = await getCache();
    const cachedRecord = cache[contentId];
    if (!cachedRecord) return;

    try {
      if (!contentServiceApi.patch) throw missingActionError(action);
      setIsSaving(true);
      const content = await contentServiceApi.patch(contentId, body);
      const clonedRecord: MediaResponse = cloneDeep(cachedRecord.object);
      clonedRecord.reverseOrder = content.data.body.reverseOrder;
      // Content search does not index synonyms, so the patch response is the only place the saved
      // values come back from. Mirroring them keeps the enrich form correct for the rest of the session.
      clonedRecord.synonyms = content.data.body.synonyms;
      // The patch response does not include contentLabels. The form reopens from this cache, so the
      // labels just sent have to be written here or the drawer shows the previous ones.
      const sentContentLabels = contentLabelsSentInPatch(body);
      if (sentContentLabels !== undefined) {
        (clonedRecord as MediaWithSentContentLabels).contentLabels = sentContentLabels;
      }
      addRecordToCache(clonedRecord);
      handleSuccess(action, getRecordString(clonedRecord));
      return clonedRecord;
    } catch (err) {
      console.error(err);
      handleApiError(err, 'patch_content', contentId);
    } finally {
      setIsSaving(false);
    }
  };

  const clearPersistedSearchParams = () => {
    // withSearchParams persists through a selector, so setting it rewrites the stored value.
    // The key has to be dropped afterwards for the search not to survive a reload.
    setSearchParams(createDefaultSearchParams());
    removeLocalStorageItem(searchParamsStorageKey(params.name));
  };

  return {
    ...dataManagerHook,
    searchMedia,
    searchMediaBySmartQuery,
    exportMediaToCSV,
    getMediaByCollectionId,
    getMediaBySmartQuery,
    getContentSubscriptionStatus,
    setContentSubscriptionStatus,
    queueIdToFetch,
    getChannelsByIds,
    getTransmissionsByIds,
    patchContent,
    clearPersistedSearchParams
  };
}
